import * as React from "react";
import * as ReactDOM from "react-dom";
import AppRouter from "./router";
import { init, captureException, captureMessage } from '@sentry/browser';
import * as Sentry from '@sentry/browser';
import { version as appVersion } from './version';
import './main.css';

// Use version from version.js
let FINAL_VERSION = appVersion || '0.1.0-dev';

let env = ""
if (process.env.NODE_ENV !== 'production') {
    console.log("dev");
    env = "development"
} else {
    console.log("prod");
    env = "production"
}

// ALWAYS enable Sentry with different environment tags
window.__SENTRY_ENABLED__ = true;

init({
    dsn: 'https://201ebbaa2c894c5392d7f6847cfa23ab@sentry.logenios.com/14',
    environment: env,
    release: FINAL_VERSION,
    maxBreadcrumbs: 50
});

// Make captureException globally available for error handlers
window.captureException = captureException;

// Set up user context with username from localStorage (will be updated after app loads)
// This runs immediately to get device fingerprint
const setupUserContext = () => {
    try {
        const logeniosAccountStr = localStorage.getItem('logenios_account');
        const deviceId = localStorage.getItem('device_identifier');

        let logeniosAccount = null;
        let userName = null;
        let userId = null;

        // Try to parse logenios_account - it might be JSON string or already an object
        if (logeniosAccountStr) {
            try {
                // Try parsing as JSON first
                const parsed = (typeof logeniosAccountStr === 'string'
                    ? JSON.parse(logeniosAccountStr)
                    : logeniosAccountStr) as any;

                // User info can be at root or nested under .user
                if (parsed?.user?._id) {
                    // Nested structure: { user: { _id, email, username, ... } }
                    userId = parsed.user._id;
                    userName = parsed.user.email || parsed.user.username;
                } else if (parsed?._id || parsed?.email || parsed?.username) {
                    // Root level structure: { _id, email, username, ... }
                    userId = parsed._id || parsed.email;
                    userName = parsed.email || parsed.username;
                }

                logeniosAccount = parsed;
            } catch (parseError) {
                // If JSON parsing fails, try to use it as-is if it's an object
                if (typeof logeniosAccountStr === 'object') {
                    const acct = logeniosAccountStr as any;
                    logeniosAccount = acct;
                    if (acct?.user?._id) {
                        userId = acct.user._id;
                        userName = acct.user.email || acct.user.username;
                    }
                } else {
                    console.log('[Sentry] logenios_account is not valid JSON, using fallback');
                    logeniosAccount = null;
                }
            }
        }

        if (userName && userId) {
            const usernamePart = userName.split('@')[0];

            // Generate custom fingerprint: username + device_identifier + browser
            const deviceFingerprint = `${usernamePart}-${deviceId || 'unknown'}-${navigator.userAgent.substring(0, 30)}`;

            Sentry.configureScope((scope) => {
                scope.setUser({
                    id: userId,
                    email: userName,
                    username: usernamePart
                });
                // Set custom fingerprint with device_identifier
                scope.setFingerprint([
                    usernamePart,
                    deviceFingerprint,
                    deviceId || '{{ default }}'
                ]);
            });

            console.log(`[Sentry] User context set: ${userName}`);
            console.log(`[Sentry] Device fingerprint: ${deviceFingerprint}`);
        } else {
            // No valid user logged in yet, set device_identifier as fallback
            if (deviceId) {
                Sentry.configureScope((scope) => {
                    scope.setUser({
                        id: deviceId,
                        username: 'anonymous'
                    });
                    scope.setFingerprint([
                        'anonymous',
                        deviceId,
                        '{{ default }}'
                    ]);
                });
                console.log(`[Sentry] Device context set (not logged in): ${deviceId}`);
            }
        }
    } catch (e) {
        console.log('[Sentry] Error in setupUserContext:', e);
    }
};

setupUserContext();

// Watch for localStorage changes and update user context when user logs in
const watchLocalStorage = () => {
    let lastAccount = localStorage.getItem('logenios_account');

    // Use a custom event listener to detect storage changes
    const checkStorageInterval = setInterval(() => {
        const currentAccount = localStorage.getItem('logenios_account');
        const deviceId = localStorage.getItem('device_identifier');

        // Only update if account changed
        if (currentAccount !== lastAccount) {
            lastAccount = currentAccount;

            let userName = null;
            let userId = null;

            if (currentAccount) {
                try {
                    const parsed = (typeof currentAccount === 'string'
                        ? JSON.parse(currentAccount)
                        : currentAccount) as any;

                    // User info can be at root or nested under .user
                    if (parsed?.user?._id) {
                        userId = parsed.user._id;
                        userName = parsed.user.email || parsed.user.username;
                    } else if (parsed?._id || parsed?.email || parsed?.username) {
                        userId = parsed._id || parsed.email;
                        userName = parsed.email || parsed.username;
                    }
                } catch (parseError) {
                    if (typeof currentAccount === 'object') {
                        const acct = currentAccount as any;
                        if (acct?.user?._id) {
                            userId = acct.user._id;
                            userName = acct.user.email || acct.user.username;
                        }
                    }
                }
            }

            if (userName && userId) {
                const usernamePart = userName.split('@')[0];

                // Generate custom fingerprint: username + device_identifier + browser
                const deviceFingerprint = `${usernamePart}-${deviceId || 'unknown'}-${navigator.userAgent.substring(0, 30)}`;

                Sentry.configureScope((scope) => {
                    scope.setUser({
                        id: userId,
                        email: userName,
                        username: usernamePart
                    });
                    // Set custom fingerprint with device_identifier
                    scope.setFingerprint([
                        usernamePart,
                        deviceFingerprint,
                        deviceId || '{{ default }}'
                    ]);
                });

                console.log(`[Sentry] User context updated: ${userName}`);
                console.log(`[Sentry] Device fingerprint updated: ${deviceFingerprint}`);

                // Stop checking after user is logged in
                clearInterval(checkStorageInterval);
            }
        }
    }, 500);

    // Stop checking after 30 seconds max
    setTimeout(() => clearInterval(checkStorageInterval), 30000);
};

watchLocalStorage();


// Log version info for debugging
console.log(`Config Manager Version: ${FINAL_VERSION}, Environment: ${env}`);

// Capture console warnings and send to Sentry with environment tags
const originalWarn = console.warn;
const originalError = console.error;

console.warn = function(...args) {
    originalWarn.apply(console, args);

    // Send warnings to Sentry
    if (window.__SENTRY_ENABLED__) {
        try {
            const message = args.map(arg =>
                typeof arg === 'string' ? arg : JSON.stringify(arg)
            ).join(' ');

            captureException(new Error(`[WARNING] ${message}`));
        } catch (e) {
            // Silent fail to avoid infinite loops
        }
    }
};

console.error = function(...args) {
    originalError.apply(console, args);

    // Send errors to Sentry
    if (window.__SENTRY_ENABLED__) {
        try {
            const message = args.map(arg =>
                typeof arg === 'string' ? arg : JSON.stringify(arg)
            ).join(' ');

            captureException(new Error(`[ERROR] ${message}`));
        } catch (e) {
            // Silent fail to avoid infinite loops
        }
    }
};


// Handle global errors including Worker failures
window.addEventListener('error', (event) => {
    if (event.message && event.message.includes('importScripts')) {
        // Capture worker loading errors with context
        if (window.__SENTRY_ENABLED__) {
            try {
                captureException(new Error(`Worker Load Error: ${event.message}`));
            } catch (e) {
                console.error("Failed to capture worker error in Sentry:", e);
            }
        }
    }
});

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && typeof event.reason === 'string' && event.reason.includes('importScripts')) {
        if (window.__SENTRY_ENABLED__) {
            try {
                captureException(new Error(`Worker Promise Rejection: ${event.reason}`));
            } catch (e) {
                console.error("Failed to capture promise rejection in Sentry:", e);
            }
        }
    }
});

ReactDOM.render(<AppRouter />,
    document.getElementById("example")
);

// Send startup message to Sentry after React renders (with delay to ensure Sentry is ready)
setTimeout(() => {
    try {
        captureMessage(`[STARTUP] Config Manager initialized - environment: ${env}, version: ${FINAL_VERSION}`);
    } catch (e) {
        console.error("Failed to send startup message to Sentry:", e);
    }
}, 1000);
